/**
 * StudentIdCardPage — printable A4-friendly sheet of student ID badges with
 * QR codes. Supports single student (`/students/:studentId/id-card`) and
 * bulk-by-branch + optional section filter (`/id-cards?branch=…&section=…`).
 *
 * Each card encodes a HMAC-signed payload. When a teacher scans the badge
 * with the Attendance Scanner page, today's attendance is auto-marked.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, GraduationCap, ScanLine } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

function studentInitials(name = "", last = "") {
  const a = (name || "").trim().charAt(0).toUpperCase();
  const b = (last || "").trim().charAt(0).toUpperCase();
  return (a + b) || "?";
}

function IdCard({ row, schoolName }) {
  const full = `${row.name} ${row.last_name || ""}`.trim();
  return (
    <div
      className="id-card relative rounded-2xl bg-white border border-[#1D4ED8]/20 overflow-hidden flex flex-col"
      style={{ width: "54mm", height: "85mm" }}
      data-testid={`id-card-${row.student_id}`}
    >
      <div
        className="h-12 px-3 py-2 text-white flex items-center gap-1.5"
        style={{ background: "linear-gradient(90deg, #1D4ED8, #2563EB)" }}
      >
        <GraduationCap className="w-3.5 h-3.5" />
        <span className="text-[10px] font-heading font-semibold uppercase tracking-wider truncate">
          {schoolName || "Student ID"}
        </span>
      </div>
      <div className="flex-1 flex flex-col items-center px-3 pt-3">
        {row.photo ? (
          <img
            src={row.photo}
            alt={full}
            className="w-16 h-16 rounded-full object-cover border-2 border-[#1D4ED8]/20"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-[#EFF6FF] border-2 border-[#1D4ED8]/20 flex items-center justify-center text-[#1D4ED8] font-heading font-bold text-lg">
            {studentInitials(row.name, row.last_name)}
          </div>
        )}
        <div className="text-center mt-1.5">
          <div className="font-heading text-[11px] font-bold text-foreground leading-tight uppercase">{full}</div>
          {row.grade_level && (
            <div className="text-[9px] text-slate-500 mt-0.5">Grade {row.grade_level}</div>
          )}
        </div>
        <div className="mt-1.5 bg-white rounded-md p-1">
          <QRCodeSVG
            value={row.payload}
            size={92}
            level="M"
            includeMargin={false}
            fgColor="#1D4ED8"
          />
        </div>
        <div className="text-[7px] text-slate-400 tracking-wider mt-1 text-center">
          ID: {(row.student_id || "").slice(0, 8).toUpperCase()}
        </div>
      </div>
    </div>
  );
}

export default function StudentIdCardPage() {
  const { studentId } = useParams();
  const [searchParams] = useSearchParams();
  const { selectedBranch, selectedSchool } = useBranch();
  const branchFromUrl = searchParams.get("branch");
  const sectionFromUrl = searchParams.get("section");

  const branchId = branchFromUrl || selectedBranch?.id;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (studentId) {
          const { data } = await api.get(`/students/${studentId}/qr-payload`);
          if (!cancelled) setRows([data]);
        } else if (branchId) {
          const params = sectionFromUrl ? { section_id: sectionFromUrl } : {};
          const { data } = await api.get(`/branches/${branchId}/students/qr-payloads`, { params });
          if (!cancelled) setRows(data?.items || []);
        }
      } catch (err) {
        toast.error(formatApiError(err));
        if (!cancelled) setRows([]);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [studentId, branchId, sectionFromUrl]);

  const schoolName = useMemo(() => selectedSchool?.name || "SchoolBoss", [selectedSchool]);

  function doPrint() { window.print(); }

  return (
    <div className="space-y-6" data-testid="student-id-card-page">
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body * { visibility: hidden; }
          #id-card-print-area, #id-card-print-area * { visibility: visible; }
          #id-card-print-area { position: absolute; left: 0; top: 0; width: 100%; }
          #id-card-toolbar { display: none !important; }
        }
      `}</style>

      <div id="id-card-toolbar" className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 print:hidden">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground tracking-tight">Student ID Cards</h1>
          <p className="text-slate-500 mt-1">
            {studentId ? "Single student badge." : "Bulk-print badges for the selected branch."} Cards include a HMAC-signed QR for one-tap attendance scanning.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/attendance/scan">
            <Button variant="outline" className="rounded-full" data-testid="open-scanner-link"><ScanLine className="w-4 h-4 mr-1.5" /> Open Scanner</Button>
          </Link>
          <Link to="/students">
            <Button variant="outline" className="rounded-full" data-testid="back-to-students-link"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</Button>
          </Link>
          <Button
            onClick={doPrint}
            disabled={rows.length === 0}
            className="rounded-full bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] text-white"
            data-testid="print-id-cards-btn"
          >
            <Printer className="w-4 h-4 mr-1.5" /> Print {rows.length > 0 && `(${rows.length})`}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-3xl border border-[#2563EB]/10 p-10 text-center text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-3xl border border-[#2563EB]/10 p-10 text-center" data-testid="id-cards-empty">
          <p className="font-heading text-lg font-semibold">No students found</p>
          <p className="text-slate-500 mt-1 text-sm">Pick a branch from the top bar or open a single student to print one badge.</p>
        </div>
      ) : (
        <div id="id-card-print-area">
          <div className="flex flex-wrap gap-3 p-2">
            {rows.map((row) => (
              <IdCard key={row.student_id} row={row} schoolName={schoolName} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
